import React from "react";
import { HomeOutlined, SearchOutlined, UserOutlined } from "@ant-design/icons";
import { Breadcrumb, Table, Input, Button, Space } from "antd";
import type { ColumnsType } from "antd/es/table";
import "./data_adjustment.css";
import useSWR from "swr";
import axios from "axios";
import { useState, useRef } from "react";
import { EVENT_OPTIONS } from "./adjustment";
import { statusMapping } from "./hoc_adjustment";
import { useNavigate } from "react-router-dom";

interface AdjustmentRecord {
    status: string;
    adjustment_no: string;
    adjustment_date: string;
    amount_total: string | number;
    warehouse: string;
    event_no: string;
}

const DataAdjustment: React.FC = () => {
    const [currentPage, setCurrentPage] = useState(1);
    const [searchText, setSearchText] = useState('');
    const [sortInfo, setSortInfo] = useState({ field: 'adjustment_no', order: 'descend' });
    const searchInput = useRef<any>(null);
    const navigate = useNavigate();

    const fetcher = (url: string) => axios.get(url).then(res => res.data);
    const { data, isLoading } = useSWR(
        `${process.env.REACT_APP_BACKEND_URL}/api/adjustment/all?page=${currentPage}&search=${searchText}&sortBy=${sortInfo.field}&sortOrder=${sortInfo.order}`,
        fetcher,
        { revalidateOnFocus: false, dedupingInterval: 5000, keepPreviousData: true }
    );

    const getColumnSearchProps = (dataIndex: string) => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: any) => (
            <div style={{ padding: 8 }}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${dataIndex}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => { setSearchText(selectedKeys[0]); confirm(); }}
                    style={{ marginBottom: 8, display: 'block' }}
                />
                <Space>
                    <Button type="primary" onClick={() => { setSearchText(selectedKeys[0]); confirm(); }} icon={<SearchOutlined />} size="small" style={{ width: 90 }}>Search</Button>
                    <Button onClick={() => { clearFilters(); setSearchText(''); confirm(); }} size="small" style={{ width: 90 }}>Reset</Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered: boolean) => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
    });

    const columns: ColumnsType<AdjustmentRecord> = [
        { title: 'Status', dataIndex: 'status', key: 'status', sorter: true, render: (val: string) => statusMapping[val] || val },
        { title: 'No Adjustment', dataIndex: 'adjustment_no', key: 'adjustment_no', sorter: true, defaultSortOrder: 'descend', ...getColumnSearchProps('adjustment_no') },
        { title: 'Date Adjustment', dataIndex: 'adjustment_date', key: 'adjustment_date', sorter: true, ...getColumnSearchProps('adjustment_date') },
        { title: 'Amount', dataIndex: 'amount_total', key: 'amount_total', sorter: true, ...getColumnSearchProps('amount_total'), render: (val: any) => parseFloat(val || 0).toLocaleString('id-ID') },
        { title: 'Warehouse', dataIndex: 'warehouse', key: 'warehouse', sorter: true, ...getColumnSearchProps('warehouse') },
        { title: 'Event No', dataIndex: 'event_no', key: 'event_no', sorter: true, ...getColumnSearchProps('event_no'), render: (val: string) => EVENT_OPTIONS.find(opt => opt.value === val)?.label || val },
    ];

    const handleTableChange = (pagination: any, filters: any, sorter: any) => {
        setCurrentPage(pagination.current);
        if (sorter.field) setSortInfo({ field: sorter.field, order: sorter.order });
    };
    const transformStyle = {
        transform: "scale(0.80)",
        transformOrigin: "top left",
        width: "125%",
    };
    return (
        <div id="ifv3" style={transformStyle}>
            <div className="gjs-grid-row" id="iu0ci">
                <div className="gjs-grid-column" id="i68pf">
                    <Breadcrumb
                        items={[
                            {
                                title: <HomeOutlined />,
                            },
                            {

                                title: (
                                    <>
                                        <span style={{ fontWeight: '200', color: 'rgba(0, 0, 0, 0.88)' }}>Data Adjustment</span>
                                    </>
                                ),
                            },
                            {
                                href: "#/dashboard/adjustment",
                                title: "Adjustment",
                            },
                        ]}
                    />
                </div>
            </div>
            <div className="gjs-grid-row" id="iczu1">
                <div className="gjs-grid-column" id="iyci2"></div>
            </div>
            <div className="gjs-grid-row" id="i2irq">
                <div className="gjs-grid-column" id="i13l4">
                    <Table
                        columns={columns}
                        dataSource={data?.data || []}
                        loading={isLoading}
                        pagination={{ current: currentPage, total: data?.total || 0, pageSize: 10 }}
                        onChange={handleTableChange}
                        rowKey="adjustment_no"
                        onRow={(record: AdjustmentRecord) => ({
                            onDoubleClick: () => navigate('/dashboard/adjustment', { state: { adjustment_no: record.adjustment_no } })
                        })}
                    />
                </div>
            </div>
        </div>
    );
};

export default DataAdjustment;
